import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import { authService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { buildRegisterPath, getSafeRedirectPath } from '../utils/redirect';

function LoginNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useTranslation();
  const theme = getThemeVars(ACTIVE_THEME);

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const afterLoginPath = useMemo(
    () => getSafeRedirectPath(searchParams.get('after_login_url') || searchParams.get('redirect'), '/'),
    [searchParams]
  );

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login(formData.email, formData.password);
      const data = response.data;
      const accessToken = data?.accessToken || data?.access_token;

      if (!accessToken) {
        throw new Error('Missing access token');
      }

      // Refresh token rides in the HttpOnly cookie set by the backend.
      await login(accessToken);
      navigate(afterLoginPath);
    } catch (err) {
      const backendMsg = err.response?.data?.message || '';
      if (backendMsg.toLowerCase().includes('invalid credentials') || backendMsg.toLowerCase().includes('not verified')) {
        setError(t('login.invalidCredentialsOrUnverified'));
      } else {
        setError(backendMsg || t('Error al iniciar sesión'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await authService.getGoogleAuthUrl(afterLoginPath);
      const payload = response?.data;
      if (payload?.authUrl) {
        window.location.href = payload.authUrl;
      } else {
        setError(t('Error: No se pudo obtener la URL de autenticación'));
      }
    } catch (err) {
      setError(t('Error al iniciar sesión con Google'));
    }
  };

  return (
    <div
      className="dashboard-new font-body min-h-[100dvh] flex items-center justify-center px-4"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <div className="w-full max-w-md">
        <div className="bg-surface rounded-xl shadow-sm border border-outline p-8">
          <h1 className="text-3xl font-bold text-center text-primary">{t('Vatuco')}</h1>
          <h2 className="text-lg font-medium text-center text-on-surface-variant mt-2">{t('Iniciar Sesión')}</h2>

          {error && (
            <div id="login-error" role="alert" className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-error-container text-on-error-container text-sm">
              <span className="material-symbols-outlined text-[18px]">error</span>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="relative">
              <input
                type="email"
                id="login-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                autoFocus
                aria-invalid={!!error}
                aria-describedby={error ? 'login-error' : undefined}
                placeholder={t('Correo Electrónico')}
                className="peer w-full border-2 border-outline-variant/30 rounded-lg px-3.5 pt-5 pb-2 text-base text-on-surface bg-white placeholder-transparent transition-colors"
              />
              <label
                htmlFor="login-email"
                className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base peer-placeholder-shown:text-on-surface-variant/60 peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-primary transition-all"
              >
                {t('Correo Electrónico')}
              </label>
            </div>

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="login-password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                aria-invalid={!!error}
                aria-describedby={error ? 'login-error' : undefined}
                placeholder={t('Contraseña')}
                className="peer w-full border-2 border-outline-variant/30 rounded-lg px-3.5 pt-5 pb-2 pr-12 text-base text-on-surface bg-white placeholder-transparent transition-colors"
              />
              <label
                htmlFor="login-password"
                className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base peer-placeholder-shown:text-on-surface-variant/60 peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-primary transition-all"
              >
                {t('Contraseña')}
              </label>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center min-w-[44px] min-h-[44px] text-on-surface-variant hover:text-on-surface transition-colors bg-transparent"
              >
                <span className="material-symbols-outlined text-[20px]">
                  {showPassword ? 'visibility_off' : 'visibility'}
                </span>
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-primary text-on-primary font-medium text-base transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {loading ? t('Iniciando sesión...') : t('Iniciar Sesión')}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-outline-variant/20" />
            <span className="text-sm text-on-surface-variant">{t('O')}</span>
            <div className="flex-1 h-px bg-outline-variant/20" />
          </div>

          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-lg border-2 border-outline-variant/30 text-on-surface font-medium text-base hover:bg-surface-container-low transition-colors bg-transparent"
          >
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            {t('Continuar con Google')}
          </button>

          <p className="text-center text-sm text-on-surface-variant mt-6">
            {t('¿No tienes cuenta?')}{' '}
            <Link to={buildRegisterPath(afterLoginPath)} className="text-primary font-medium hover:underline">
              {t('Regístrate')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginNew;
